<template>
  <el-dialog
    title="Add User"
    :visible.sync="show"
    @close="resetModal"
    :close-on-click-modal="false"
  >
    <el-form @keyup.enter.native="handleSubmit">
      <el-form-item label="First Name">
        <el-input
          v-model="$v.firstName.$model"
          :class="{ error: checkValid && $v.firstName.$invalid }"
          @blur="setDisplayName"
        />
      </el-form-item>
      <el-form-item label="Last Name">
        <el-input
          v-model="$v.lastName.$model"
          :class="{ error: checkValid && $v.lastName.$invalid }"
          @blur="setDisplayName"
        />
      </el-form-item>
      <el-form-item>
        <span slot="label">
          Display Name
          <small>
            <el-tooltip
              class="modal-info"
              placement="right"
              effect="light"
              content="Appears throughout the application to publicly identify the user"
            >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-input
          v-model="$v.displayName.$model"
          :class="{ error: checkValid && $v.displayName.$invalid }"
        />
      </el-form-item>
      <el-form-item label="Email">
        <el-input
          v-model="$v.email.$model"
          :class="{ error: checkValid && $v.email.$invalid }"
        />
      </el-form-item>
      <el-form-item>
        <span slot="label">
          Password
          <small>
            <el-tooltip
              class="modal-info"
              placement="right"
              effect="light"
              content="Between 8 and 32 characters in length, contains only alphanumeric characters, and must contain at least one lowercase/uppercase/number"
            >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-input
          v-model="$v.password.$model"
          type="password"
          show-password
          :class="{ error: checkValid && $v.password.$invalid }"
        />
      </el-form-item>
      <el-form-item>
        <span slot="label">
          Organization Role
          <small>
            <el-tooltip
              class="modal-info"
              placement="right"
              effect="light"
              content="Determines the user’s ability to manage DataMaestro users, environments, and servers"
            >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-select
          filterable
          style="width: 100%"
          v-model="$v.role.$model"
          :class="{ error: checkValid && $v.role.$invalid }"
          placeholder
        >
          <el-option label="Administrator" value="Administrator" />
          <el-option label="Standard User" value="Standard" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <span slot="label">
          Environments
          <small>
            <el-tooltip
              class="modal-info"
              placement="right"
              effect="light"
              content="Determines the user's ability to create, modify, and run both tasks and packages"
            >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-table
          :data="items"
          :default-sort="{ prop: 'name', order: 'ascending' }"
          style="border-top: 1px solid #ebeef5; margin: none"
          size="mini"
        >
          <el-table-column prop="name" label="Friendly Name" sortable />
          <el-table-column
            prop="adminHost"
            label="Admin Host or IBM Environment"
            sortable
          />
          <el-table-column label="Environment Rights" width="250">
            <template slot-scope="scope">
              <el-select filterable v-model="scope.row.rights">
                <el-option :value="null" label="No Access" />
                <el-option value="Contribute" label="Contribute and Run" />
                <el-option value="Run" label="Run Only" />
              </el-select>
            </template>
          </el-table-column>
        </el-table>
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button class="button" @click="handleSubmit">Save</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapState } from "vuex";
import AdministrationService from "../../services/AdministrationService";
import {
  required,
  email,
  alphaNum,
  minLength,
  maxLength,
} from "vuelidate/lib/validators";
import Helper from "../../utilities/Helper";

/**
 * @vue-data {String} [firstName=""] - First name of the new user
 * @vue-data {String} [lastName=""] - Last name of the new user
 * @vue-data {String} [displayName=""] - Display name of the new user
 * @vue-data {String} [role=""] - Organization role of the new user
 * @vue-data {String} [error=""] - Error message from backend
 * @vue-data {Boolean} [checkValid=false] - Flag that indicates whether to check for errors
 */
export default {
  props: {
    isVisible: Boolean,
  },
  watch: {
    isVisible(value) {
      this.show = value;
    },
  },
  data() {
    return {
      firstName: null,
      lastName: null,
      displayName: null,
      email: null,
      password: null,
      role: null,
      checkValid: false,
      items: [],
      fields: [
        {
          key: "name",
          label: "Friendly Name",
        },
        {
          key: "adminHost",
          label: "Admin Host or IBM Environment",
        },
        {
          key: "rights",
          label: "Access",
        },
      ],
      show: this.isVisible,
    };
  },
  computed: {
    ...mapState(["organizationId", "instanceId"]),
  },
  methods: {
    handleError: Helper.handleError,
    trim: Helper.trim,
    /**
     * Sets the default display name
     * @function
     */
    setDisplayName() {
      if (this.firstName && this.lastName && !this.displayName) {
        this.displayName = `${this.firstName} ${this.lastName}`;
      }
    },
    /**
     * Fetches the list of environments
     * @function
     */
    async getOptions() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        const response = await AdministrationService.showEnvironments({
          organizationId: this.organizationId,
        });
        this.items = response.data.environments;
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    /**
     * Resets the modal
     * @function
     */
    resetModal() {
      this.firstName = null;
      this.lastName = null;
      this.displayName = null;
      this.email = null;
      this.password = null;
      this.role = null;
      this.checkValid = false;

      this.getOptions();

      this.$emit("update:isVisible", false);
    },
    /**
     * Submits the new user information
     * @async
     * @function
     */
    async handleSubmit() {
      this.checkValid = true;
      if (
        this.$v.firstName.$invalid ||
        this.$v.lastName.$invalid ||
        this.$v.displayName.$invalid ||
        this.$v.email.$invalid ||
        this.$v.password.$invalid ||
        this.$v.role.$invalid
      ) {
        this.$message({
          type: "warning",
          message: "Invalid user credentials",
          duration: 7000,
          showClose: true,
        });
        return;
      }

      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        await AdministrationService.create({
          organizationId: this.organizationId,
          firstName: this.trim(this.firstName),
          lastName: this.trim(this.lastName),
          displayName: this.trim(this.displayName),
          email: this.email,
          password: this.password,
          role: this.role,
          environments: this.items,
        });
        this.$emit("update:isVisible", false);
        this.$message({
          type: "success",
          message:
            "The new user was created successfully. They will receive a confirmation email and can reset their password at any time from the login page.",
          duration: 5000,
          showClose: true,
        });
        this.checkValid = false;
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
  },
  mounted() {
    this.getOptions();
  },
  validations() {
    return {
      firstName: { required },
      lastName: { required },
      displayName: { required },
      email: { required, email },
      password: {
        required,
        alphaNum,
        minLength: minLength(8),
        maxLength: maxLength(32),
        meetsRequirements() {
          const regex = new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/);
          return regex.test(this.password);
        },
      },
      role: { required },
    };
  },
};
</script>

<style scoped>
.error :deep(.el-input__inner) {
  border: 1px solid red;
  border-radius: 4px;
}
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.cancel-button {
  background-color: #7a7c80;
  color: white;
  border-color: white;
}
.cancel-button:focus {
  background-color: #606266;
  color: white;
  border-color: white;
  outline: none;
}
.cancel-button:hover {
  background-color: #606266;
  color: white;
  border-color: white;
  outline: none;
}
</style>
